import React, { useEffect, useState, useContext, useMemo } from 'react'
import styles from './accounts-details.module.scss'
import Button from 'src/components//button/Button'
import Input from 'src/components//input/Input'
import { IUser, IAccount, IState } from 'src/Interface'
import axios from 'axios'
import { baseApiURL } from 'src/utils/utils'
import { StateContext } from '../../AppContext'
import MultiValueInput from '../../components/multiValueInput/MultiValueInput'
import { toast } from 'react-toastify'
import ToggleSwitch from 'src/components/toggleSwitch/ToggleSwitch'
import { validString } from 'src/utils/utils'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'

interface IValues {
  name: string
  code: string
  userIds: Array<number>
}

interface IError {
  accountName: boolean
  code: boolean
}

interface IProps {
  close?: any
  companyId: number
  accountId: number
}

const UserDetails: React.FunctionComponent<IProps> = ({ close, companyId, accountId }: IProps) => {
  const state: IState = useContext(StateContext)
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const dispatch = useContext(DispatchContext)

  const [accountInfo, setAccountInfo] = useState<IAccount>({
    id: null,
    name: '',
    defaultAccount: false,
    code: '',
    status: '0',
    usersWithAccess: []
  })
  const [users, setUsers] = useState<Array<IUser>>([])
  const [selectedUsers, updateSelectedUsers] = useState([])
  const [isDefaultAccount, setDefaultAccount] = useState(false)
  const { cancellablePromise } = useCancellablePromise()
  const usersWithAccess = useMemo(() => users.filter(user => accountInfo.usersWithAccess.find((u: IUser) => user.id === u.id)), [accountInfo.usersWithAccess, users])
  const [values, setValues] = useState<IValues>({
    name: '',
    code: '',
    userIds: []
  })

  const [errors, setErrors] = useState<IError>({
    accountName: false,
    code: false,
  })

  useEffect(() => {
    if (state.user.accessToken, companyId) {
      cancellablePromise(axios(`${baseApiURL()}/users${`?companyId=${companyId}`}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setUsers(result.data)
      }).catch((err) => {
        toast(err.response.data.message)
      }))

      cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/accounts/${accountId}`,
        {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        }).then((result: any) => {
          setAccountInfo(result.data)
          setValues(result.data)
          setDefaultAccount(result.data.defaultAccount)
          updateSelectedUsers(result.data.usersWithAccess)
          setPageLoading(false)
        }).catch((err: any) => {
          toast(err.response.data.message)
        }))
    }
  }, [accountId, companyId, state.user.accessToken])

  const udateAccountInfo = () => {
    if (state.user.accessToken) {
      setLoading(true)
      axios.put(`${baseApiURL()}/companies/${companyId}/accounts/${accountId}`,
        {
          name: values.name ? values.name : null,
          code: values.code ? values.code : null,
          userIds: selectedUsers.map(user => user.id),
          defaultAccount: isDefaultAccount,

        }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        axios.get(`${baseApiURL()}/companies/${companyId}/accounts`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
          .then((result) => {
            dispatch({ type: 'SET_ACCOUNTS', accounts: result.data })
            close()
          }).catch((err) => {
            toast(<svg className='tw-w-5 tw-h-5 tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg> + err.response.data.message)
            setLoading(false)
          })
      }).catch((err: any) => {
        toast(err.response.data.message)
        
        setLoading(false)
      })
    }
  }

  const handleInputChange = (fieldName: string) => (value: string) => {
    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
  }

  const activateAccount = () => {
    setLoading(true)
    axios.put(`${baseApiURL()}/companies/${companyId}/accounts/${accountId}/activate`,
      {},
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        axios.get(`${baseApiURL()}/companies/${companyId}/accounts`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
          .then((result) => {
            dispatch({ type: 'SET_ACCOUNTS', accounts: result.data })
            close()
          }).catch((err) => {
            toast(<svg className='tw-w-5 tw-h-5 tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg> + err.response.data.message)
            setLoading(false)
          })
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false

    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }
    setErrors({
      ...errors,
      [fieldName]: error
    })
  }

  const deleteAccount = () => {
    setDeleteLoading(true)
    axios.delete(`${baseApiURL()}/companies/${companyId}/accounts/${accountId}`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        axios.get(`${baseApiURL()}/companies/${companyId}/accounts`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
          .then((result) => {
            dispatch({ type: 'SET_ACCOUNTS', accounts: result.data })
            dispatch({ type: 'SHOW_MODAL', showModal: false })
            setDeleteLoading(true)
            close()
          }).catch((err) => {
            toast(<svg className='tw-w-5 tw-h-5 tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg> + err.response.data.message)
            setDeleteLoading(false)
          })
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
  }

  if (pageLoading) {
    return null
  }

  return (
    <>
      <header className={styles.header}>
        <h3>{`${accountInfo.name}`}</h3>
        <span className={styles.closeIcon} onClick={close}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </header>
      <div className={styles.container}>
        <Input
          modifiers='tw-mb-3'
          value={values.name}
          label='Navn'
          type='text'
          error={errors.accountName}
          resetError={() => resetError('accountName')}
          handleValidation={() => handleValidation(validString, 'accountName')}
          handleInputChange={() => handleInputChange('name')}
          name='accountName'
        />
        <Input
          modifiers='tw-mb-3'
          value={values.code}
          label='Konto-kode'
          type='text'
          error={errors.code}
          resetError={() => resetError('code')}
          handleValidation={() => handleValidation(validString, 'code')}
          handleInputChange={() => handleInputChange('code')}
          name='code'
        />
        <MultiValueInput selectedValues={usersWithAccess} label='Medarbejdere' updateList={updateSelectedUsers} placeholder='Tilføj medarbejdere”' name='users' list={users} />
        <div className={styles.defaultAccount}>
          <ToggleSwitch checked={isDefaultAccount} label='Standard konto' onChange={setDefaultAccount} />
        </div>
      </div>
      <footer className={styles.footer}>
        {accountInfo.status === 'ACTIVE' && <Button loading={deleteLoading} type='destructive' label='Fjern' modifiers='tw-mr-5 tw-ml-auto' handleClick={() => dispatch({ type: "SHOW_MODAL", showModal: true, modalType: 'deleteConfirmation', title: 'Fjern konto', text: '<p>Er du sikker på at du vil fjerne denne konto?</p> <p>Dette kan ikke fortrydes.</p>', buttonText: 'Fjern konto', handleDelete: deleteAccount })} />}
        <Button loading={loading} type='primary' label={accountInfo.status === 'ARCHIVED' ? 'Aktivér' : 'Gem ændringer'} handleClick={accountInfo.status === 'ARCHIVED' ? activateAccount : udateAccountInfo} />
      </footer>
    </>
  )
}

export default UserDetails
